import axios from 'axios';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaBuilding, FaCheck, FaChevronLeft, FaChevronRight, FaIdCard, FaLandmark, FaRegUser } from 'react-icons/fa';
import logo from '../assets/images/LogoMercadito.png';
import { useAppDispatch } from '../hooks';
import { RootState } from '../store';
import {
  clearProviderApplicationDraft,
  ProviderApplicationDraft,
  setProviderApplicationStep,
  updateProviderDraft,
} from '../slices/providerApplication';
import './providerWorkflow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const steps = ['Tu cuenta', 'Plan', 'Identidad', 'Cuenta bancaria', 'Términos'];
type FileState = {
  identityFront: File | null; identityBack: File | null; identitySelfie: File | null;
};

const initialFiles: FileState = {
  identityFront: null, identityBack: null, identitySelfie: null,
};

type UploadCardProps = {
  label: string;
  file: File | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const UploadCard = ({ label, file, onChange }: UploadCardProps) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPreviewUrl('');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return <label className={`provider-upload ${file ? 'has-file' : ''}`}>
    {previewUrl
      ? <img className="provider-upload__preview" src={previewUrl} alt={`Vista previa de ${label}`} />
      : <div className="provider-upload__placeholder"><FaIdCard /><strong>{label}</strong><small>JPG, PNG o PDF · Máx. 5 MB</small></div>}
    <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={onChange} />
    <span className="provider-upload__filename">{file ? file.name : 'Seleccionar archivo'}</span>
    {file && <small className="provider-upload__replace">Haz clic para reemplazar</small>}
  </label>;
};

const ProviderApplicationPage = () => {
  const dispatch = useAppDispatch();
  const { step, draft } = useSelector((state: RootState) => state.providerApplication);
  const [files, setFiles] = useState<FileState>(initialFiles);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [submittedSummary, setSubmittedSummary] = useState({ name: '', email: '' });
  const form = { ...draft, ...files };

  const displayName = form.accountType === 'BUSINESS' ? form.businessName : form.fullName;
  const update = (field: keyof ProviderApplicationDraft, value: string | boolean) =>
    dispatch(updateProviderDraft({ field, value }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.accountType) return 'Selecciona el tipo de cuenta.';
      if (form.accountType === 'PERSON' && !form.fullName.trim()) return 'Ingresa tu nombre completo.';
      if (form.accountType === 'BUSINESS' && (!form.businessName.trim() || !form.representativeName.trim())) return 'Completa el negocio y su representante.';
      if (!/^\S+@\S+\.\S+$/.test(form.email) || !form.phone.trim() || !form.description.trim()) return 'Completa un correo, teléfono y descripción válidos.';
    }
    if (step === 1 && !form.plan) return 'Selecciona un plan.';
    if (step === 2 && (!form.identityFront || !form.identityBack || !form.identitySelfie)) return 'Adjunta las tres fotografías requeridas.';
    if (step === 3 && !/^CR\d{20}$/.test(form.iban.replace(/\s/g, '').toUpperCase())) return 'El IBAN debe iniciar con CR y contener 20 dígitos.';
    if (step === 4 && !form.termsAccepted) return 'Debes aceptar los términos y condiciones.';
    return '';
  };

  const next = () => {
    const validationError = validateStep();
    if (validationError) return setError(validationError);
    setError('');
    dispatch(setProviderApplicationStep(Math.min(step + 1, steps.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fileInput = (field: 'identityFront' | 'identityBack' | 'identitySelfie') =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFiles(current => ({ ...current, [field]: event.target.files?.[0] || null }));
      setError('');
    };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const validationError = validateStep();
    if (validationError) return setError(validationError);
    setSubmitting(true);
    setError('');
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value !== null && value !== '') body.append(key, value instanceof File ? value : String(value));
    });
    try {
      const response = await axios.post(`${API_URL}/provider-applications`, body);
      setSubmittedSummary({ name: displayName, email: form.email });
      setApplicationId(response.data.id);
      dispatch(clearProviderApplicationDraft());
      setFiles(initialFiles);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No pudimos enviar la solicitud. Inténtalo nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (applicationId) {
    return <main className="provider-workflow provider-success">
      <div className="provider-success__card">
        <div className="provider-success__check"><FaCheck /></div>
        <span>Solicitud recibida</span>
        <h1>¡Gracias, {submittedSummary.name}!</h1>
        <p>Tu solicitud <strong>#{applicationId}</strong> fue enviada. También enviamos una confirmación a <strong>{submittedSummary.email}</strong>.</p>
        <p>Nuestro equipo revisará la información y se pondrá en contacto contigo.</p>
        <Link to="/" className="provider-primary-button">Volver al inicio</Link>
      </div>
    </main>;
  }

  return <main className="provider-workflow">
    <header className="provider-workflow__header container">
      <Link to="/" className="provider-brand"><img src={logo} alt="El Mercadito de Kila" /><span>El Mercadito de Kila</span></Link>
      <Link to="/" className="provider-back-link"><FaChevronLeft /> Volver a la tienda</Link>
    </header>
    <section className="provider-workflow__hero container">
      <span>Programa para emprendedores</span>
      <h1>Convierte tu talento en una vitrina para toda Costa Rica</h1>
      <p>Completa estos cinco pasos. Puedes revisar la información antes de enviarla.</p>
    </section>

    <div className="provider-stepper container" aria-label="Progreso de solicitud">
      {steps.map((label, index) => <div className={`provider-step ${index < step ? 'is-complete' : ''} ${index === step ? 'is-active' : ''}`} key={label}>
        <div className="provider-step__number">{index < step ? <FaCheck /> : index + 1}</div>
        <span>{label}</span>
      </div>)}
    </div>

    <form className="provider-form container" onSubmit={submit}>
      <div className="provider-form__card">
        {step === 0 && <section>
          <div className="provider-form__heading"><span>Paso 1 de 5</span><h2>Cuéntanos sobre tu emprendimiento</h2><p>Primero selecciona cómo deseas registrarte.</p></div>
          <div className="provider-choice-grid">
            <button type="button" className={`provider-choice ${form.accountType === 'PERSON' ? 'is-selected' : ''}`} onClick={() => update('accountType', 'PERSON')}><FaRegUser /><strong>Persona física</strong><small>Para vender bajo tu nombre personal.</small></button>
            <button type="button" className={`provider-choice ${form.accountType === 'BUSINESS' ? 'is-selected' : ''}`} onClick={() => update('accountType', 'BUSINESS')}><FaBuilding /><strong>Tienda / negocio</strong><small>Para una marca, tienda o emprendimiento.</small></button>
          </div>
          <div className="provider-fields">
            {form.accountType === 'PERSON' && <label className="provider-field provider-field--wide"><span>Nombre completo *</span><input value={form.fullName} onChange={e => update('fullName', e.target.value)} /></label>}
            {form.accountType === 'BUSINESS' && <><label className="provider-field"><span>Nombre del negocio *</span><input value={form.businessName} onChange={e => update('businessName', e.target.value)} /></label><label className="provider-field"><span>Nombre del representante *</span><input value={form.representativeName} onChange={e => update('representativeName', e.target.value)} /></label></>}
            <label className="provider-field"><span>Correo electrónico *</span><input type="email" value={form.email} onChange={e => update('email', e.target.value)} /></label>
            <label className="provider-field"><span>Teléfono *</span><input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="8888-8888" /></label>
            <label className="provider-field provider-field--wide"><span>¿Qué producto o servicio ofreces? *</span><textarea rows={4} value={form.description} onChange={e => update('description', e.target.value)} /></label>
            {form.accountType === 'BUSINESS' && <label className="provider-check provider-field--wide"><input type="checkbox" checked={form.storePickupEnabled} onChange={e => update('storePickupEnabled', e.target.checked)} /><span><strong>Habilitar retiro en tienda</strong><small>Tengo un local físico donde los clientes pueden retirar sus compras.</small></span></label>}
          </div>
        </section>}

        {step === 1 && <section><div className="provider-form__heading"><span>Paso 2 de 5</span><h2>Elige el plan ideal para ti</h2><p>Podrás conocer y ajustar los detalles comerciales antes de activar tu cuenta.</p></div><div className="provider-plan-grid">
          <button type="button" className={`provider-plan ${form.plan === 'BASIC' ? 'is-selected' : ''}`} onClick={() => update('plan', 'BASIC')}><span>Básico</span><h3>Empieza tu vitrina</h3><ul><li>Publicación de productos</li><li>Panel de administración</li><li>Soporte estándar</li></ul><strong>Seleccionar Básico</strong></button>
          <button type="button" className={`provider-plan provider-plan--featured ${form.plan === 'PREMIUM' ? 'is-selected' : ''}`} onClick={() => update('plan', 'PREMIUM')}><span>Premium · Recomendado</span><h3>Impulsa tu negocio</h3><ul><li>Todo lo incluido en Básico</li><li>Mayor visibilidad en la tienda</li><li>Herramientas y soporte prioritario</li></ul><strong>Seleccionar Premium</strong></button>
        </div></section>}

        {step === 2 && <section><div className="provider-form__heading"><span>Paso 3 de 5</span><h2>Verificación de identidad</h2><p>La información se almacena de forma privada y solo será utilizada para validar tu solicitud.</p></div><div className="provider-upload-grid">
          <UploadCard label="Cédula · frente" file={form.identityFront} onChange={fileInput('identityFront')} />
          <UploadCard label="Cédula · reverso" file={form.identityBack} onChange={fileInput('identityBack')} />
          <UploadCard label="Selfie con la cédula" file={form.identitySelfie} onChange={fileInput('identitySelfie')} />
        </div></section>}

        {step === 3 && <section><div className="provider-form__heading"><span>Paso 4 de 5</span><h2>Cuenta bancaria</h2><p>La cuenta IBAN es obligatoria para todos los planes y será utilizada para futuros pagos.</p></div><div className="provider-bank-card"><FaLandmark /><label className="provider-field provider-field--wide"><span>IBAN de Costa Rica *</span><input value={form.iban} onChange={e => update('iban', e.target.value.toUpperCase())} placeholder="CR00 0000 0000 0000 0000 00" maxLength={27} /><small>Debe iniciar con CR y pertenecer al solicitante o negocio registrado.</small></label></div></section>}

        {step === 4 && <section><div className="provider-form__heading"><span>Paso 5 de 5</span><h2>Términos y confirmación</h2><p>Revisa el resumen antes de enviar tu solicitud.</p></div><div className="provider-summary"><div><span>Solicitante</span><strong>{displayName}</strong></div><div><span>Tipo de cuenta</span><strong>{form.accountType === 'PERSON' ? 'Persona física' : 'Tienda / negocio'}</strong></div><div><span>Plan</span><strong>{form.plan === 'BASIC' ? 'Básico' : 'Premium'}</strong></div><div><span>Contacto</span><strong>{form.email}</strong></div></div><div className="provider-terms"><h3>Términos y condiciones para proveedores</h3><p>Declaro que la información suministrada es verdadera, que cuento con capacidad para comercializar los productos o servicios descritos y autorizo a El Mercadito de Kila a verificarla para evaluar esta solicitud.</p><p>Comprendo que enviar la solicitud no garantiza su aprobación ni crea todavía una cuenta de proveedor.</p></div><label className="provider-check"><input type="checkbox" checked={form.termsAccepted} onChange={e => update('termsAccepted', e.target.checked)} /><span><strong>Acepto los términos y condiciones de la plataforma.</strong></span></label></section>}

        {error && <div className="provider-error" role="alert">{error}</div>}
        <div className="provider-form__actions">
          {step > 0 ? <button type="button" className="provider-secondary-button" onClick={() => { setError(''); dispatch(setProviderApplicationStep(step - 1)); }}><FaChevronLeft /> Anterior</button> : <span />}
          {step < steps.length - 1 ? <button type="button" className="provider-primary-button" onClick={next}>Continuar <FaChevronRight /></button> : <button className="provider-primary-button" type="submit" disabled={submitting}>{submitting ? 'Enviando…' : 'Enviar solicitud'}</button>}
        </div>
      </div>
    </form>
  </main>;
};

export default ProviderApplicationPage;
